import React from 'react';
import { Button, Modal, Select } from 'semantic-ui-react';
import { DateRangePicker } from 'react-dates';
import 'react-dates/initialize';
import 'react-dates/lib/css/_datepicker.css';
import Swal from 'sweetalert2';
import reportService from 'src/services/api/reportService';
import moment from 'src/services/moment_instance';
import {
  eventSlugnameTrim,
  formSelectService,
  generacionesServices,
  searchSelectProps,
} from 'src/components/helpers';
import { renderMonthElement } from 'src/components/form/Form';

const ALL_CATEGORIES = 'all';

const defaultDateRange = () => ({
  startDate: moment().startOf('day').isoWeekday(1),
  endDate: moment().endOf('day').isoWeekday(8),
});

const categoryOptions = [
  { key: ALL_CATEGORIES, value: ALL_CATEGORIES, text: 'Todas' },
  ...generacionesServices.map((opt) => ({
    key: opt,
    value: opt,
    text: eventSlugnameTrim(opt),
  })),
];

const GeneracionesServicesReport = (props) => {
  const {
    previousEventsNames = [],
    initialEventSlugname,
    initialCategory,
    initialHandleDate,
  } = props;
  const [open, setOpen] = React.useState(false);
  const [focused, set_focused] = React.useState(null);
  const [loading, set_loading] = React.useState(false);
  const [inputs, setInputs] = React.useState({});
  const [handleDate, set_handleDate] = React.useState(defaultDateRange());

  const resetFilters = () => {
    setInputs({
      eventSlugname: initialEventSlugname || '',
      generacionesCategory: initialCategory || ALL_CATEGORIES,
    });
    set_handleDate({
      startDate: initialHandleDate?.startDate
        ? moment(initialHandleDate.startDate)
        : defaultDateRange().startDate,
      endDate: initialHandleDate?.endDate
        ? moment(initialHandleDate.endDate)
        : defaultDateRange().endDate,
    });
    set_focused(null);
  };

  const handleDateChange = (startDate, endDate) => {
    setInputs((prev) => ({
      ...prev,
      startDate: startDate ? startDate.clone().startOf('day')._d.getTime() : null,
      endDate: endDate ? endDate.clone().endOf('day')._d.getTime() : null,
    }));
    set_handleDate({ startDate, endDate });
  };

  const selectHandle = (a, { value, name }) => {
    setInputs((prev) => ({ ...prev, [name]: value }));
  };

  const downloadReport = () => {
    const { eventSlugname, generacionesCategory, startDate, endDate } = inputs;

    if (!eventSlugname) {
      return Swal.fire('Selecciona un evento');
    }

    if (!handleDate.startDate || !handleDate.endDate) {
      return Swal.fire('Selecciona un rango de fechas');
    }

    set_loading(true);

    const category = generacionesCategory || ALL_CATEGORIES;
    const fileName = `InformeGeneraciones_${eventSlugname}_${category}.xlsx`;

    reportService
      .getReport({
        URL: '/generaciones/serviceReport',
        fileName,
        body: {
          eventSlugname,
          generacionesCategory: category,
          startDate: startDate || handleDate.startDate.toDate(),
          endDate: endDate || handleDate.endDate.toDate(),
        },
      })
      .catch((error) => {
        if (!error.response) return Swal.fire('No se pudo descargar el reporte.');
        try {
          const decoded = JSON.parse(
            String.fromCharCode.apply(null, new Uint8Array(error.response.data))
          );
          Swal.fire(decoded.message || 'No se pudo descargar el reporte.');
        } catch {
          if (error.response.data?.message) Swal.fire(error.response.data.message);
          else Swal.fire('No se pudo descargar el reporte.');
        }
      })
      .finally(() => set_loading(false));
  };

  return (
    <Modal
      onClose={() => setOpen(false)}
      onOpen={() => {
        resetFilters();
        setOpen(true);
      }}
      open={open}
      autoFocus={false}
      trigger={<Button>Descargar reporte</Button>}
    >
      <Modal.Header>Descargar reporte de generaciones</Modal.Header>
      <Modal.Content style={{ overflow: 'visible' }}>
        <Modal.Description>
          <h2>Filtros</h2>
          <div style={{ margin: '12px 0' }}>
            <label htmlFor="eventSlugname" style={{ display: 'block', marginBottom: '6px' }}>
              Evento
            </label>
            {formSelectService({
              Select,
              label: 'Evento',
              name: 'eventSlugname',
              handler: selectHandle,
              value: inputs.eventSlugname || '',
              options: previousEventsNames,
            })}
          </div>
          <div style={{ margin: '12px 0' }}>
            <label htmlFor="generacionesCategory" style={{ display: 'block', marginBottom: '6px' }}>
              Categoria
            </label>
            <Select
              placeholder="Categoria"
              id="generacionesCategory"
              className="Registration__input Registration__input-generacionesCategory"
              {...searchSelectProps({
                name: 'generacionesCategory',
                value: inputs.generacionesCategory || ALL_CATEGORIES,
                handler: selectHandle,
                options: categoryOptions,
              })}
            />
          </div>
          <div
            style={{
              display: 'inline-block',
              margin: '12px 0',
              minHeight: focused ? 380 : undefined,
            }}
          >
            <label htmlFor="date" style={{ display: 'block', marginBottom: '6px' }}>
              Fechas
            </label>
            <DateRangePicker
              startDatePlaceholderText="Fecha Inicio"
              endDatePlaceholderText="Fecha Fin"
              startDate={handleDate.startDate}
              startDateId="generacionesReportStartDate"
              minimumNights={0}
              endDate={handleDate.endDate}
              endDateId="generacionesReportEndDate"
              onDatesChange={({ startDate, endDate }) => handleDateChange(startDate, endDate)}
              focusedInput={focused}
              onFocusChange={(focusedInput) => set_focused(focusedInput)}
              isOutsideRange={() => false}
              renderMonthElement={renderMonthElement}
              numberOfMonths={1}
              showDefaultInputIcon
              displayFormat="DD/MM/YYYY"
            />
          </div>
        </Modal.Description>
      </Modal.Content>
      <Modal.Actions>
        <Button
          disabled={loading}
          onClick={() => {
            setOpen(false);
            resetFilters();
          }}
        >
          Cerrar
        </Button>
        <Button
          style={{ margin: '5px' }}
          secondary
          disabled={loading}
          loading={loading}
          onClick={downloadReport}
        >
          Descargar reporte
        </Button>
      </Modal.Actions>
    </Modal>
  );
};

export default GeneracionesServicesReport;
